// Must match MEDIA_MAX_UPLOAD_SIZE in partners-middleware.
export const MAX_UPLOAD_SIZE_MB = 100;
export const MAX_UPLOAD_SIZE = MAX_UPLOAD_SIZE_MB * 1024 * 1024;

export type UploadKind = 'image' | 'video';

// MIME types the media library accepts, with their display label. Stricter than the
// backend whitelist, which also accepts MOV, AVI, BMP and others.
const SUPPORTED_UPLOAD_TYPES: Record<string, string> = {
  'image/jpeg': 'JPG',
  'image/png': 'PNG',
  'image/gif': 'GIF',
  'image/webp': 'WEBP',
  'image/x-icon': 'ICO',
  'image/vnd.microsoft.icon': 'ICO',
  'video/mp4': 'MP4',
  'video/webm': 'WEBM',
};

/** A translation key and its {0}, {1}... values. */
export interface UploadRejection {
  key: string;
  values: any[];
}

function supportedMimeTypes(kind?: UploadKind): string[] {
  return Object.keys(SUPPORTED_UPLOAD_TYPES).filter(mime => !kind || mime.startsWith(`${kind}/`));
}

/** Value for the dropzone's accept attribute. */
export function uploadAccept(kind?: UploadKind): string {
  return supportedMimeTypes(kind).join(',');
}

/** Display list, e.g. "JPG, PNG, GIF, WEBP, ICO". */
export function supportedUploadFormats(kind?: UploadKind): string {
  const labels = supportedMimeTypes(kind).map(mime => SUPPORTED_UPLOAD_TYPES[mime]);
  return Array.from(new Set(labels)).join(', ');
}

export function bytesToMB(bytes: number): number {
  return +(bytes / (1024 * 1024)).toFixed(2);
}

// ISO-BMFF files open with an ftyp box; major brand "qt  " marks a QuickTime movie.
function isQuickTimeFile(file: File): Promise<boolean> {
  return new Promise(resolve => {
    const reader = new FileReader();
    reader.onload = () => {
      const header = new Uint8Array(reader.result as ArrayBuffer);
      resolve(String.fromCharCode(...Array.from(header.subarray(4, 12))) === 'ftypqt  ');
    };
    reader.onerror = () => resolve(false);
    reader.readAsArrayBuffer(file.slice(0, 12));
  });
}

/**
 * Why a file can't be uploaded, or null when it can. MP4s are sniffed because a
 * QuickTime movie saved with an .mp4 name still reports video/mp4.
 */
export async function getUploadRejection(
  file: File,
  kind?: UploadKind,
  maxBytes: number = MAX_UPLOAD_SIZE
): Promise<UploadRejection | null> {
  if (!supportedMimeTypes(kind).includes(file.type)) {
    return {
      key: '{0} is not a supported format. Supported formats: {1}',
      values: [file.name, supportedUploadFormats(kind)],
    };
  }
  if (file.size > maxBytes) {
    return { key: '{0} exceeds the {1}MB upload limit', values: [file.name, bytesToMB(maxBytes)] };
  }
  if (file.type === 'video/mp4' && (await isQuickTimeFile(file))) {
    return {
      key: '{0} is a QuickTime (MOV) video. Please export it as a standard MP4 and try again.',
      values: [file.name],
    };
  }
  return null;
}
